import { create } from "zustand";
import type { MeResponse, RegisterBody, SubscriptionDto, UserDto } from "@tasduvar/shared";
import { api, API_BASE, NetworkError, onUnauthorized } from "../api/client";

export type AuthStatus = "unknown" | "guest" | "authed" | "no-api";

export interface AuthState {
  status: AuthStatus;
  user: UserDto | null;
  subscription: SubscriptionDto | null;
  /** WhatsApp numara doğrulaması bekleniyor (ücretsiz katman; sunucu söyler) */
  phoneRequired: boolean;
  /** İlk yükleme: /me → authed | guest; sunucu yoksa no-api (v1.0 misafir modu). */
  load(): Promise<void>;
  refresh(): Promise<void>;
  login(email: string, password: string, remember?: boolean): Promise<void>;
  register(body: RegisterBody): Promise<{ verifyToken?: string }>;
  logout(): Promise<void>;
  forgot(email: string): Promise<void>;
  reset(token: string, password: string): Promise<void>;
  deleteAccount(password?: string): Promise<void>;
  setMe(me: MeResponse | null): void;
  googleUrl: string;
}

export const useAuth = create<AuthState>((set, get) => ({
  status: "unknown", user: null, subscription: null, phoneRequired: false,
  googleUrl: `${API_BASE}/auth/google`,
  setMe(me) { set(me ? { status: "authed", user: me.user, subscription: me.subscription, phoneRequired: me.phoneRequired ?? false } : { status: "guest", user: null, subscription: null, phoneRequired: false }); },
  async load() {
    try { get().setMe(await api<MeResponse>("GET", "/auth/me")); }
    catch (e) { set(e instanceof NetworkError ? { status: "no-api", user: null, subscription: null } : { status: "guest", user: null, subscription: null }); }
  },
  async refresh() { if (get().status === "authed") await get().load(); },
  async login(email, password, remember) { await api("POST", "/auth/login", { email, password, ...(remember === undefined ? {} : { remember }) }); await get().load(); },
  async register(body) { return api<{ verifyToken?: string }>("POST", "/auth/register", body); },
  async logout() { try { await api("POST", "/auth/logout"); } finally { get().setMe(null); } },
  async forgot(email) { await api("POST", "/auth/forgot", { email }); },
  async reset(token, password) { await api("POST", "/auth/reset", { token, password }); },
  async deleteAccount(password) { await api("DELETE", "/auth/me", password ? { password } : {}); get().setMe(null); },
}));

onUnauthorized(() => { if (useAuth.getState().status === "authed") useAuth.getState().setMe(null); });

/** API var mı (authed ya da guest)? no-api ise v1.0 davranışı. */
export const useHasApi = (): boolean => useAuth((s) => s.status === "authed" || s.status === "guest");
export const useIsAdmin = (): boolean => useAuth((s) => s.user?.role === "admin");
